import { model as router } from '!/router'
import {
  CategoryId,
  epgCategoriesModel,
  type TvCategoryEx,
} from '$/epg/categories'
import { PATH, SEGMENT } from '@/constants'
import { type IUrlParams } from '@/types'
import { A } from '@mobily/ts-belt'
import { combine, createEvent, createStore, restore, sample } from 'effector'
import { buildEpgUrl, parseEpgPath, updateEpgPath } from '~/entities/epg/lib'
import { epgRegionsModel } from '~/entities/epg/regions'
import { model as session } from '~/entities/session'
import { loadEpgCategoriesModel } from '~/features/epg/categories/load'
import { selectEpgCategoryModel } from '~/features/epg/categories/select'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { getRegionIdFromSegment } from '~/features/epg/regions/select'
import { $route } from '~/processes/navigation'
import { addTitleToCategory } from './helpers'

// load categories list
export const init = createEvent<IUrlParams>()

// `null` means the URL carried no usable region segment. 0 and CategoryId.All are real ids,
// so they cannot be used to mean "no value".
export const $initData = createStore<{ regionId: number | null } | null>(
  null
).on(init, (_, params) => ({
  regionId: getRegionIdFromSegment(params[SEGMENT.REGION]) ?? null,
}))

// reset widget on epg page left
export const left = createEvent()

// reset widget on sign out
export const reset = createEvent()

// same as `reset`, but the selected category id and the remembered init params survive.
// used only by the region change on the EPG board, see `resetEpgKeepingCategory` in ~/widgets/epg
export const resetKeepingSelectedCategory = createEvent()

// select category
export const select = selectEpgCategoryModel.setActiveId

// categories
export const $categories = createStore<TvCategoryEx[]>([])

// categories with category "My List" by condition of authentication
export const $filteredCategories = combine(
  $categories,
  session.$isAuthenticated,
  (categories, isAuthenticated) =>
    isAuthenticated
      ? categories
      : categories.filter((category) => category.id !== CategoryId.Favorite)
)

// currently selected category id
export const $selectedId = selectEpgCategoryModel.$activeId

// load categories list by segment in url
sample({
  clock: [init, epgRegionsModel.$isRegionsLoaded],
  source: {
    initData: $initData,
    route: $route,
    regions: epgRegionsModel.$regionsAlphabeticallySorted,
    isRegionsLoaded: epgRegionsModel.$isRegionsLoaded,
  },
  filter: ({ initData, route, regions, isRegionsLoaded }) => {
    const regionId = initData?.regionId
    if (route?.routeName === `${PATH.LIVE}${PATH.CATEGORIES}`) {
      // Skip loading categories at EPG board if the region ID from URL is not in the supported regions list.
      // This prevents loading when user navigates to EPG board with an unsupported region ID.
      return Boolean(
        regions &&
          regionId &&
          regions.find((region) => region.id === regionId) &&
          isRegionsLoaded
      )
    }
    return Boolean(regionId)
  },
  fn: ({ initData }) => {
    // ILoadParams treats a missing region as `undefined`, the store uses `null`
    return { regionId: initData?.regionId ?? undefined }
  },
  target: loadEpgCategoriesModel.load,
})

// Make navigation if channel segment is invalid (f.e. /bbc))
sample({
  clock: router.$pathname,
  filter: (pathname) => {
    const { channelId, channelTitle } = parseEpgPath(pathname ?? '')

    return Boolean(channelTitle && !channelId)
  },
  fn: (pathname) => {
    return updateEpgPath(pathname, {
      categoryId: null,
      channelId: null,
    })
  },
  target: router.navigatePush,
})

// auto select first or url-chosen category
sample({
  clock: [init, loadEpgCategoriesModel.$ready],
  source: {
    init: restore(init, null).reset(left, reset),
    ready: loadEpgCategoriesModel.$ready,
  },
  filter: ({ init, ready }) => init != null && ready,

  fn: ({ init }) => {
    return { [SEGMENT.CATEGORY]: init?.[SEGMENT.CATEGORY] }
  },
  target: selectEpgCategoryModel.autoSetActiveId,
})

// wait for categories list to be loaded, then concat made-up and loaded categories
// also update categories list on load more categories event
sample({
  clock: [loadEpgCategoriesModel.$ready, epgCategoriesModel.$loaded],
  source: {
    madeup: epgCategoriesModel.$madeup,
    loaded: epgCategoriesModel.$loaded,
  },
  filter: loadEpgCategoriesModel.$ready,
  fn: ({ madeup, loaded }) =>
    A.concat(A.map(madeup, addTitleToCategory), loaded),
  target: $categories,
})

export const navigateToAllCategory = createEvent()
// remove segment channel from url and navigate to category ALL when change active
// region in dropdown to prevent show channel in tv box from previous region
// or when sign out and My list tab was selected
sample({
  clock: navigateToAllCategory,
  source: router.$pathname,
  filter: (pathname) => {
    return pathname?.startsWith(`${PATH.LIVE}${PATH.CATEGORIES}`) ?? false
  },
  fn: (pathname) => {
    const { regionId, regionTitle } = parseEpgPath(pathname ?? '')

    return buildEpgUrl({
      regionId,
      regionTitle,
      categoryId: CategoryId.All,
      channelId: null,
    })
  },
  target: router.navigatePush,
})

/* If the session ends and My list tab was selected => navigate to All tab and reset channel
also should be created epg_category events */
export const sendEpgAllCategory = createEvent()

// clocked on `sessionLost`, not on `session.$isAuthenticated`: that store also flips to `false`
// when a guest session resolves, which would bounce a guest off the My List category
sample({
  clock: session.sessionLost,
  source: selectEpgCategoryModel.$activeId,
  filter: (activeId) => activeId === CategoryId.Favorite,
  target: [
    navigateToAllCategory,
    selectEpgChannelModel.resetChannel,
    sendEpgAllCategory,
  ],
})

sample({
  clock: [reset, resetKeepingSelectedCategory],
  target: [
    epgCategoriesModel.$loaded.reinit,
    epgCategoriesModel.$madeup.reinit,
    loadEpgCategoriesModel.$ready.reinit,
    $categories.reinit,
  ],
})

sample({
  clock: reset,
  target: selectEpgCategoryModel.$activeId.reinit,
})
